import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ListCronJobsParams } from '../../../infrastructure/CronJobClient';
import { CronJobClient, CronJobFilterStatus } from '../../../infrastructure/CronJobClient';
import type { HTTPCronJob, CronJobStatus, CreateCronJobRequest, UpdateCronJobRequest } from '../../../models/CronJob';
import { getConfig } from '../../../config';

const cronJobClient = new CronJobClient(getConfig().apiUrl);

export const useCronJob = () => {
  const queryClient = useQueryClient();

  const cronJobsQuery = useQuery({
    queryKey: ['cronJobs'],
    queryFn: () => cronJobClient.listCronJobs({ status: CronJobFilterStatus.ENABLED }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const createCronJobMutation = useMutation({
    mutationFn: (request: CreateCronJobRequest) => cronJobClient.createCronJob(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
    },
  });

  const updateCronJobMutation = useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateCronJobRequest }) =>
      cronJobClient.updateCronJob(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
    },
  });

  const updateCronJobStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CronJobStatus }) =>
      cronJobClient.updateCronJobStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
    },
  });

  const deleteCronJobMutation = useMutation({
    mutationFn: (id: string) => cronJobClient.deleteCronJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cronJobs'] });
    },
  });

  const getCronJobQuery = (id: string) =>
    useQuery({
      queryKey: ['cronJob', id],
      queryFn: () => cronJobClient.getCronJob(id),
      enabled: Boolean(id),
    });

  const listCronJobsWithFilter = (params: ListCronJobsParams) =>
    useQuery({
      queryKey: ['cronJobs', params],
      queryFn: () => cronJobClient.listCronJobs(params),
      staleTime: 2 * 60 * 1000, // 2 minutes for filtered results
    });

  return {
    // Queries
    cronJobsQuery,
    getCronJobQuery,
    listCronJobsWithFilter,

    // Mutations
    createCronJobMutation,
    updateCronJobMutation,
    updateCronJobStatusMutation,
    deleteCronJobMutation,

    // Direct client access for specific needs
    cronJobClient,
  };
};

export type {
  HTTPCronJob,
  CronJobStatus,
  CreateCronJobRequest,
  UpdateCronJobRequest,
  CronJobFilterStatus,
  ListCronJobsParams,
};
